import { artScale, iconCell, type IconManifest } from "odyssey-shared";
import type { ArtBox, IconArt } from "../../ui/kit/ArtIcon.tsx";

/**
 * Which is drawn in a place that can hold colour art, the art or the glyph (ADR-111 decisions 12 and 16). Art is drawn
 * when the page has a pointer and a decoded atlas, the pointer holds the thing's key and the art fits its box at the UI
 * scale (k = `artScale(box, d)` above 0); the glyph is drawn otherwise. Pure but for the log it is handed: a thing with
 * no key is said once per thing, a key the pointer lacks once per key and k = 0 once per page, never on every draw. A
 * pointer or an atlas that did not load is the loader's to say, once, when it fails (`page-icons.ts`).
 */

/**
 * The decoded atlas: its address, its size in its own pixels, and the decoded image when the page decoded it itself,
 * which the canvas takes as a texture so the emoji over characters draw from it without fetching it again.
 */
export interface AtlasImage {
  readonly url: string;
  readonly width: number;
  readonly height: number;
  readonly element?: HTMLImageElement;
}

/**
 * Where the page's icons stand: still loading, failed (no pointer, a pointer that does not read, or an atlas that did
 * not load or decode), or ready with the pointer and the decoded atlas.
 */
export type IconAtlasState = { readonly status: "loading" } | { readonly status: "failed" } | { readonly status: "ready"; readonly manifest: IconManifest; readonly image: AtlasImage };

/**
 * What a place reads to choose: the atlas's state and the UI scale d.
 */
export interface IconsSnapshot {
  readonly atlas: IconAtlasState;
  readonly scale: number;
}

/**
 * A log that says each topic once for the life of the page.
 */
export class FallbackLog {
  private readonly said = new Set<string>();

  /**
   * @param say Where a line goes; the console's warnings unless a test hands in another.
   */
  public constructor(private readonly say: (line: string) => void = (line) => console.warn(line)) {}

  /**
   * Says a line unless its topic was said before.
   *
   * @param topic What the line is about, once per page.
   * @param line The line.
   */
  public once(topic: string, line: string): void {
    if (this.said.has(topic)) {
      return;
    }
    this.said.add(topic);
    this.say(line);
  }
}

/**
 * The art a place draws for a thing, or null for its glyph.
 *
 * @param snapshot The atlas's state and the UI scale.
 * @param key The thing's icon key, or null for a thing with none.
 * @param thing The thing as a line names it (`item minor_healing_potion`).
 * @param box The box the place scales art for.
 * @param log Where a fallback is said, once per topic.
 * @param options `needsFit`: whether the art must fit the box whole at the UI scale (`artScale` above 0), true unless
 *   the place draws it downscaled where it does not, as the staff badge does; `instead`: what is drawn when there is no
 *   art, said in the log, `its glyph is drawn` unless the place draws nothing.
 * @returns The picture, or null when the glyph is drawn.
 * @example
 * iconArt({ atlas: { status: "loading" }, scale: 3 }, "flask-red-tilted", "item minor_healing_potion", 22, log);
 * // null until the atlas has decoded
 */
export function iconArt(snapshot: IconsSnapshot, key: string | null, thing: string, box: ArtBox, log: FallbackLog, options: { readonly needsFit?: boolean; readonly instead?: string } = {}): IconArt | null {
  const needsFit = options.needsFit ?? true;
  const instead = options.instead ?? "its glyph is drawn";
  if (key === null) {
    log.once(`none ${thing}`, `${thing} has no icon; ${instead}`);
    return null;
  }
  const atlas = snapshot.atlas;
  if (atlas.status !== "ready") {
    return null;
  }
  if (needsFit && artScale(box, snapshot.scale) === 0) {
    log.once("scale", `icon art is not drawn at UI scale ${snapshot.scale}; glyphs are drawn`);
    return null;
  }
  const place = iconCell(atlas.manifest, key);
  if (place === null) {
    log.once(`key ${key}`, `the icon pointer has no ${key} (${thing}); ${instead}`);
    return null;
  }
  return { key, url: atlas.image.url, width: atlas.image.width, height: atlas.image.height, x: place.x, y: place.y, box };
}
